import { QuantitySelectorComponent } from '@theme/component-quantity-selector';
import { parseIntOrDefault } from '@theme/utilities';

/**
 * A custom element that allows the user to select a quantity in the cart.
 * Extends QuantitySelectorComponent but uses absolute max limits instead of effective max.
 * Semantics: "What should the total quantity BE in the cart" vs "How many to ADD to cart"
 *
 * @extends {QuantitySelectorComponent}
 */
class CartQuantitySelectorComponent extends QuantitySelectorComponent {
  /**
   * Gets the effective maximum value for cart quantity selector
   * Cart page: uses absolute max (how much can be in cart total)
   * @returns {number | null} The effective max, or null if no max
   */
  getEffectiveMax() {
    const { max } = this.getCurrentValues();
    return max; // Cart uses absolute max, not max minus cart quantity
  }

  /**
   * The variant's real quantity-rule minimum.
   *
   * Cart selectors render min="0" so a line can be zeroed out to remove it,
   * which means the input's own min is not the rule minimum. The rule value is
   * carried separately in data-min. Falls back to the input's min, so contexts
   * that do render the rule minimum directly (the cart page) are unaffected.
   *
   * @returns {number}
   */
  getRuleMin() {
    const { min } = this.getCurrentValues();
    return parseIntOrDefault(this.refs.quantityInput.dataset.min, min);
  }

  /**
   * Steps the quantity, respecting the rule minimum.
   *
   * Stepping up from below the rule minimum lands on the minimum itself rather
   * than on `increment`: with a B2B minimum of 8, clicking + from 0 must reach
   * 8, not 1, or the cart rejects the update. Stepping down to anything under
   * the minimum goes to the floor instead, so the line is removed rather than
   * left at a quantity the cart will refuse.
   *
   * @param {number} stepMultiplier - Positive to increase, negative to decrease
   */
  updateQuantity(stepMultiplier) {
    const { quantityInput } = this.refs;
    const { min, step, value } = this.getCurrentValues();
    const ruleMin = this.getRuleMin();
    const effectiveMax = this.getEffectiveMax();

    let newValue;
    if (stepMultiplier > 0 && value < ruleMin) {
      newValue = ruleMin;
    } else if (stepMultiplier < 0 && value - step < ruleMin) {
      newValue = min;
    } else {
      newValue = value + step * stepMultiplier;
    }

    newValue = Math.min(effectiveMax ?? Infinity, Math.max(min, newValue));

    quantityInput.value = newValue.toString();
    this.onQuantityChange();
    this.updateButtonStates();
  }

  /**
   * Validates a typed quantity, snapping up to the rule minimum.
   *
   * Typing 3 against a minimum of 8 would otherwise be sent as-is and rejected
   * by the cart. Zero is left alone because it means "remove".
   *
   * @param {Event} event - The blur event
   */
  setQuantity(event) {
    if (!(event.target instanceof HTMLInputElement)) return;

    const ruleMin = this.getRuleMin();
    const typed = parseInt(event.target.value) || 0;

    if (typed > 0 && typed < ruleMin) {
      event.target.value = ruleMin.toString();
    }

    super.setQuantity(event);
  }

  /**
   * Updates button states based on current value and limits
   * Cart buttons are always managed client-side, never server-disabled
   */
  updateButtonStates() {
    const { minusButton, plusButton } = this.refs;
    const { min, value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    // Cart buttons are always dynamically managed
    minusButton.disabled = value <= min;
    plusButton.disabled = effectiveMax !== null && value >= effectiveMax;
  }
}

if (!customElements.get('cart-quantity-selector-component')) {
  customElements.define('cart-quantity-selector-component', CartQuantitySelectorComponent);
}
